//* Import libraries
import { useState, useCallback } from "react";

//* Import react-query hooks
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

//* Import API
import { studentAPI } from "./studentAPI";

//* Import Pagination type
import { type Pagination } from "../../services/stores/types";

//* Import types from types
import {
  type StudentsDetails,
  type CreateStudentInput,
  type UpdateStudentInput,
} from "./types/types";

//* Import types from CSVStudent-validator
import {
  type CSVStudent,
  type CSVStudentProcessingError,
  type CSVStudentProcessingResult,
} from "../../utils/csv/csv-validatorStudent";

export const useStudents = (token: string) => {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  const queryKey = [
    "students",
    {
      search,
      currentPage: pagination.currentPage,
      itemsPerPage: pagination.itemsPerPage,
    },
  ] as const;

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      studentAPI.getAll(
        search,
        pagination.currentPage,
        token,
        pagination.itemsPerPage
      ),
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const students = data?.students || [];
  const fetchedPagination = data?.pagination || pagination;

  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const changePage = useCallback((newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  const createStudentMutation = useMutation({
    mutationFn: (studantData: CreateStudentInput) =>
      studentAPI.create(studantData, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["students"] });
    },
  });

  const updateStudentMutation = useMutation({
    mutationFn: ({ id, studentData }: UpdateStudentInput) =>
      studentAPI.update(id, studentData, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["students"] });
    },
  });

  const deleteStudentMutation = useMutation({
    mutationFn: (id: string) => studentAPI.delete(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["students"] });
    },
  });

  const getStudentById = useCallback(
    async (id: string): Promise<StudentsDetails | null> => {
      try {
        const StudentDetails = await studentAPI.getById(id, token);
        return StudentDetails;
      } catch (err) {
        console.error("Error fetching Student by ID:", err);
        return null;
      }
    },
    [token]
  );

  const processCSVStudentMutation = useMutation<
    CSVStudentProcessingResult,
    Error,
    CSVStudent[]
  >({
    mutationFn: async (
      students: CSVStudent[]
    ): Promise<CSVStudentProcessingResult> => {
      try {
        const result = await studentAPI.createBatch(students, token);

        return {
          success: result.success,
          errors: result.errors.map((err: any) => ({
            id: err.id,
            student: {
              name: err.student.name,
              email: err.student.email,
              isValid: false,
              is_restore: err.is_restore,
            },
            error: err.error || "Unknown error",
          })),
          total: result.total,
        };
      } catch (error: any) {
        // Captura os erros retornados pelo backend
        let errorMessage = "Erro desconhecido";
        let errors: CSVStudentProcessingError[] = [];

        if (
          error.response &&
          error.response.data &&
          error.response.data.error
        ) {
          const backendError = error.response.data.error;
          errorMessage = backendError.message || errorMessage;

          // Se o backend retornar erros detalhados
          if (backendError.errors && Array.isArray(backendError.errors)) {
            errors = backendError.errors;
          }
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }

        return {
          success: 0,
          errors:
            errors.length > 0
              ? errors.map((err) => ({
                  id: err.id,
                  student: {
                    name: err.student?.name || "Nome não disponível",
                    email: err.student?.email || "Email não disponível",
                    isValid: false,
                  },
                  error: err.error || errorMessage,
                  is_restore: err.student.is_restore,
                }))
              : students.map((student, index) => ({
                  id: `error-${index}`,
                  student: { ...student, isValid: false },
                  error: errorMessage,
                  is_restore: false,
                })),
          total: students.length,
        };
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["students"] });
    },
  });

  const restoreStudentByBatchMutation = useMutation({
    mutationFn: (id: string) => studentAPI.restoreByBatch(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["students"] });
    },
  });

  const restoreByBatchStudentByEmailMutation = useMutation({
    mutationFn: (email: string) => studentAPI.restoreByEmail(email, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["students"] });
    },
  });

  const restoreStudentByEmailMutation = useMutation({
    mutationFn: (email: string) => studentAPI.restoreByEmail(email, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["students"] });
    },
  });

  return {
    students,
    loading: isLoading || isFetching,
    error: isError ? error?.message : null,
    search,
    setSearch: handleSearch,
    pagination: fetchedPagination,
    setPagination,
    refetch,
    createStudentAsync: createStudentMutation.mutateAsync,
    updateStudent: updateStudentMutation.mutate,
    deleteStudent: deleteStudentMutation.mutate,
    getStudentById,
    changePage,
    isCreating: createStudentMutation.isPending,
    isUpdating: updateStudentMutation.isPending,
    isDeleting: deleteStudentMutation.isPending,
    processCSVStudent: processCSVStudentMutation.mutateAsync,
    isProcessingCSVStudent: processCSVStudentMutation.isPending,
    CSVStudentError: processCSVStudentMutation.error,
    restoreByBatchStudent: restoreStudentByBatchMutation.mutate,
    restoreByEmailStudent: restoreStudentByEmailMutation.mutate,
  };
};
